import { useState } from "react";
import { useContext } from "react";
import {
    Button, Modal, ModalHeader, ModalBody,
    FormGroup, Label, Input, Alert
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import { importProducts, downloadImportTemplate } from "../../api/api";
import Loader from "../../components/Loader";

const ImportProductsButton = () => {
    const { refreshToken } = useContext(AuthContext);
    const [modal, setModal] = useState(false);
    const [file, setFile] = useState(null);
    const [importErrors, setImportErrors] = useState([]);
    const [importSummary, setImportSummary] = useState(null);
    const toggleModal = () => {
        setModal(!modal);
        setFile(null);
        setImportErrors([]);
        setImportSummary(null);
    };

    const queryClient = useQueryClient();

    const importMutation = useMutation({
        mutationFn: importProducts,
        onSuccess: (data) => {
            queryClient.invalidateQueries(['products']);
            setImportSummary(data.data);
            setImportErrors(data.data.errors || []);
            if (data.data.error_count === 0) {
                toast.success(`Successfully imported ${data.data.created_count} products!`);
                toggleModal();
            } else {
                toast.warning(`Imported ${data.data.created_count} products with ${data.data.error_count} errors.`);
            }
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, importMutation, variables);
            if (err.response?.status !== 401) {
                toast.error(`Import failed: ${err?.response?.data?.detail}`);
            }
        }
    });

    const handleImport = (e) => {
        e.preventDefault();
        if (!file) return toast.error('Please select a file.');
        const formData = new FormData();
        formData.append('file', file);
        importMutation.mutate(formData);
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await downloadImportTemplate();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'products_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast.error('Failed to download template.');
        }
    };

    return (
        <>
            <div
                className="btn bx-btn-primary waves-effect waves-light mb-3 ml-2"
                onClick={toggleModal}
            >
                <i className="mdi mdi-file-import mr-1" />
                Import Products
            </div>

            <Modal isOpen={modal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal} close={
                    <button className="close" onClick={toggleModal}>&times;</button>
                }>
                    Import Products from CSV / Excel
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted font-13">
                        Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file. Max <strong>500 products</strong> per import...
                        Columns: <code>name, price, description, upc, hsn_sac, unit_of_measure, tax_type, tax_percentage, is_digital, is_inventory_tracking_enabled, stock, status</code>
                    </p>

                    <Button
                        size="sm"
                        color="link"
                        className="p-0 mb-3"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="mdi mdi-download mr-1" />
                        Download Template
                    </Button>

                    <form onSubmit={handleImport}>
                        <FormGroup>
                            <Label for="importFile">Select File<span className="text-danger">*</span></Label>
                            <Input
                                type="file"
                                id="importFile"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => {
                                    setFile(e.target.files[0]);
                                    setImportErrors([]);
                                    setImportSummary(null);
                                }}
                            />
                        </FormGroup>

                        {importSummary && (
                            <Alert color={importSummary.error_count > 0 ? 'warning' : 'success'}>
                                ✓ {importSummary.created_count} imported
                                {importSummary.error_count > 0 && `, ${importSummary.error_count} failed`}
                            </Alert>
                        )}

                        {importErrors.length > 0 && (
                            <div className="mt-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                <p className="text-danger font-13 mb-1"><strong>Errors:</strong></p>
                                {importErrors.map((err, idx) => (
                                    <div key={idx} className="mb-2 font-12 text-danger">
                                        <strong>Row {err.row}:</strong>{' '}
                                        {Object.entries(err.errors).map(([field, msgs]) =>
                                            `${field}: ${msgs.join(', ')}`
                                        ).join(' | ')}
                                    </div>
                                ))}
                            </div>
                        )}

                        <Button
                            className="bx-btn-primary"
                            block
                            disabled={importMutation.isLoading || !file}
                        >
                            {importMutation.isLoading
                                ? <><Loader size={'small'} /> Importing...</>
                                : 'Import'}
                        </Button>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default ImportProductsButton;